import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Plus,
  Mail, 
  Lock, 
  Phone, 
  User,
  Package,
  Scissors,
  Calendar,
  CreditCard,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { CustomerAuth } from '../auth/CustomerAuth';
import { ServiceSelector } from './ServiceSelector';
import { StaffDateTimeSelector } from './StaffDateTimeSelector';
import { AppointmentSummary } from './AppointmentSummary';
import { supabase } from '../../lib/supabase';
import { format, addDays, isSameDay } from 'date-fns';
import { he } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface BookingWizardProps {
  businessId: string;
  onClose: () => void;
  onSuccess: (appointmentId: string) => void;
  staffFilter?: (user: any) => boolean;
}

type Step = 'customer' | 'service' | 'datetime' | 'summary';

interface ExistingAppointment {
  id: string;
  start_time: string;
  service_name: string;
  staff_name: string;
  is_same_day: boolean;
}

export function BookingWizard({ businessId, onClose, onSuccess, staffFilter }: BookingWizardProps) {
  const [currentStep, setCurrentStep] = useState<Step>('service');
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [showExistingAppointmentModal, setShowExistingAppointmentModal] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState<ExistingAppointment | null>(null);
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceId: '',
    serviceName: '',
    servicePrice: '',
    serviceDuration: '00:30:00', // ברירת מחדל - 30 דקות
    staffId: '',
    staffName: '',
    date: new Date(),
    time: '09:00'
  });
  const [pendingServiceId, setPendingServiceId] = useState(''); // אופציונלי

  // Hide bottom nav when modal is open
  useEffect(() => {
    (window as any).setModalOpen?.(true);
    return () => {
      (window as any).setModalOpen?.(false);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (formData.serviceId) {
      setCurrentStep('datetime');
    }
  }, [formData.serviceId]);

 const checkAuth = async () => {

  let customerPhone = localStorage.getItem('customerPhone');

  if (!customerPhone) {

    setCustomerData(null);
    return;
  }

  // נסה למצוא עם הפורמט המקורי
  try {

    let { data: customer, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('phone', customerPhone)
      .maybeSingle();

    // אם לא נמצא, נסה עם פורמט חלופי
    if (!customer && !error) {
      let alternativePhone = customerPhone;

      // אם מתחיל ב-+972, נסה גם עם 0
      if (customerPhone.startsWith('+972')) {
        alternativePhone = '0' + customerPhone.slice(4);

      }
      // אם מתחיל ב-0, נסה גם עם +972
      else if (customerPhone.startsWith('0')) {
        alternativePhone = '+972' + customerPhone.slice(1);

      }

      const result = await supabase
        .from('customers')
        .select('*')
        .eq('business_id', businessId)
        .eq('phone', alternativePhone)
        .maybeSingle();

      customer = result.data;
      error = result.error;
    }

    if (error || !customer) {

      localStorage.removeItem('customerPhone');
      setCustomerData(null);
      return;
    }

    setCustomerData(customer);
    setFormData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      customerEmail: customer.email
    }));
  } catch (error) {

    setCustomerData(null);
  }
};

  const handleServiceSelect = async (serviceId: string) => {
    if (!customerData) {
      setShowAuth(true);
      setFormData(prev => ({ ...prev, serviceId }));
      setPendingServiceId(serviceId); // אופציונלי
      return;
    }

    try {
      // ודא שהשירות קיים
      if (!serviceId) {
        toast.error('לא נבחר שירות');
        return;
      }

      // שלוף את השירות מתוך formData אם כבר נטען, אחרת מה-db
      let service = null;
      if (serviceId && Array.isArray(formData.services)) {
        service = formData.services.find((s: any) => s.id === serviceId);
      }
      if (!service) {
        const { data, error } = await supabase
          .from('services')
          .select('name_he, price, duration')
          .eq('id', serviceId)
          .eq('business_id', businessId)
          .single();
        if (error || !data) {
          toast.error('שגיאה בטעינת פרטי השירות');
          return;
        }
        service = data;
      }

      let finalPrice = service.price;
      let finalDuration = service.duration;

      if (formData.staffId) {
        const { data: staffService, error: staffServiceError } = await supabase
          .from('staff_services')
          .select('price, duration')
          .eq('staff_id', formData.staffId)
          .eq('service_id', serviceId)
          .single();

        if (staffServiceError && staffServiceError.code !== 'PGRST116') throw staffServiceError;

        if (staffService) {
          finalPrice = staffService.price || finalPrice;
          finalDuration = staffService.duration || finalDuration;
        }
      }

      setFormData(prev => ({
        ...prev,
        serviceId,
        serviceName: service.name_he,
        servicePrice: finalPrice?.toString() ?? '',
        serviceDuration: finalDuration ?? '00:30:00'
      }));
      setPendingServiceId(''); // אופציונלי

      // אל תעשה כאן setCurrentStep('datetime') — ה-useEffect יטפל בזה
    } catch (error: any) {

      toast.error('שגיאה בטעינת פרטי השירות');
    }
  };

  const handleDateTimeSelect = async (date: Date, time: string, staffId: string) => {
    if (!staffId) {
      toast.error('יש לבחור איש צוות');
      return;
    }

    if (!time) {
      try {
        const { data: staff, error } = await supabase
          .from('users')
          .select('name')
          .eq('id', staffId)
          .single();

        if (error) throw error;

        const { data: staffService, error: staffServiceError } = await supabase
          .from('staff_services')
          .select('price, duration')
          .eq('staff_id', staffId)
          .eq('service_id', formData.serviceId)
          .single();

        if (staffServiceError && staffServiceError.code !== 'PGRST116') throw staffServiceError;

        setFormData(prev => ({ 
          ...prev, 
          staffId,
          staffName: staff.name,
          servicePrice: staffService?.price?.toString() || prev.servicePrice,
          serviceDuration: staffService?.duration || prev.serviceDuration
        }));
      } catch (error: any) {

        toast.error('שגיאה בטעינת פרטי איש הצוות');
      }
      return;
    }

    try {
      const { data: staff, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', staffId)
        .single();

      if (error) throw error;

      const { data: staffService, error: staffServiceError } = await supabase
        .from('staff_services')
        .select('price, duration')
        .eq('staff_id', staffId)
        .eq('service_id', formData.serviceId)
        .single();

      if (staffServiceError && staffServiceError.code !== 'PGRST116') throw staffServiceError;

      const [hours, minutes] = time.split(':').map(Number);
      const appointmentDate = new Date(date);
      appointmentDate.setHours(hours, minutes, 0, 0);

      setFormData(prev => ({ 
        ...prev, 
        date: appointmentDate,
        time,
        staffId,
        staffName: staff.name,
        servicePrice: staffService?.price?.toString() || prev.servicePrice,
        serviceDuration: staffService?.duration || prev.serviceDuration
      }));
      setCurrentStep('summary');
    } catch (error: any) {

      toast.error('שגיאה בטעינת פרטי איש הצוות');
    }
  };

  const handleConfirm = async () => {
    if (!customerData) {
      setShowAuth(true);
      return;
    }

    try {
      setLoading(true);

      // בדיקת תורים קיימים
      const existingAppointment = await checkExistingAppointments();
      if (existingAppointment) {
        setExistingAppointment(existingAppointment);
        setShowExistingAppointmentModal(true);
        setLoading(false);
        return;
      }

      await createAppointment();
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בקביעת התור');
      setLoading(false);
    }
  };

  const checkExistingAppointments = async () => {
    if (!customerData?.id) return null;

    try {
      // בדיקת תורים קיימים ב-3 ימים הקרובים
      const futureDate = addDays(new Date(), 3);
      
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          services (
            name_he
          ),
          users (
            name
          )
        `)
        .eq('business_id', businessId)
        .eq('customer_id', customerData.id)
        .in('status', ['booked', 'confirmed'])
        .gte('start_time', new Date().toISOString())
        .lte('start_time', futureDate.toISOString())
        .order('start_time')
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        return {
          id: data[0].id,
          start_time: data[0].start_time,
          service_name: data[0].services.name_he,
          staff_name: data[0].users.name,
          is_same_day: isSameDay(new Date(data[0].start_time), formData.date)
        };
      }

      return null;
    } catch (error) {

      return null;
    }
  };

  const createAppointment = async (shouldCancelExisting: boolean = false) => {
    try {
      setLoading(true);

      // אם צריך לבטל תור קיים
      if (shouldCancelExisting && existingAppointment) {
        const customerPhone = localStorage.getItem('customerPhone');
        if (!customerPhone) {
          throw new Error('לא נמצא מספר טלפון של הלקוח');
        }

        const { error: cancelError } = await supabase.rpc('cancel_appointment', {
          appointment_id: existingAppointment.id,
          customer_phone: customerPhone,
          reason: 'ביטול אוטומטי עקב קביעת תור חדש'
        });
        console.log('cancel_appointment data:', data)
console.log('cancel_appointment error:', error)

        if (cancelError) {

          throw new Error('שגיאה בביטול התור הקיים');
        }
      }

      // חישוב זמן סיום לפי זמן שירות מותאם
      const startTime = formData.date;

      const durationMatch = formData.serviceDuration.match(/(\d+):(\d+):(\d+)/);

      let durationMinutes = 30; // ברירת מחדל
      if (durationMatch) {
        const [_, hours, minutes, seconds] = durationMatch;
        if (parseInt(hours) > 0 || parseInt(minutes) > 0) {
          durationMinutes = parseInt(hours) * 60 + parseInt(minutes);
        }
        else if (parseInt(seconds) > 0) {
          durationMinutes = parseInt(seconds);
        }
      }

      const endTime = new Date(startTime.getTime() + durationMinutes * 60000);

      // Create appointment

      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .insert({
          business_id: businessId,
          customer_id: customerData.id,
          service_id: formData.serviceId,
          staff_id: formData.staffId,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          status: 'booked',
          metadata: {
            created_by: 'customer',
            created_at: new Date().toISOString()
          }
        })
        .select()
        .single();

      if (appointmentError) {

        throw appointmentError;
      }

      onSuccess(appointment.id);
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בקביעת התור');
    } finally {
      setLoading(false);
      setShowExistingAppointmentModal(false);
    }
  };

  // הוסף פונקציה עזר לבדוק אם שעה מסוימת נמצאת בהפסקה
  function isInBreak(hour: number, minute: number, breaks: { startTime?: string; endTime?: string; start_time?: string; end_time?: string }[]) {
    const slotMinutes = hour * 60 + minute;
    return breaks.some(b => {
      const start = (b.startTime || b.start_time || '').split(':');
      const end = (b.endTime || b.end_time || '').split(':');
      if (start.length !== 2 || end.length !== 2) return false;
      const startM = Number(start[0]) * 60 + Number(start[1]);
      const endM = Number(end[0]) * 60 + Number(end[1]);
      return slotMinutes >= startM && slotMinutes < endM;
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-x-0 bottom-0 bg-white overflow-hidden flex flex-col modal-content"
        style={{
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          maxHeight: '90vh',
          boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          overscrollBehavior: 'contain',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: '#D1D5DB' }}
          />
        </div>

        {/* Header */}
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #F3F4F6' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold" style={{ color: '#1C1C1E' }}>
                {currentStep === 'service' ? 'בחר שירות' :
                 currentStep === 'datetime' ? 'בחר מועד' :
                 'סיכום התור'}
              </h2>
              <p className="text-sm mt-0.5" style={{ color: '#8E8E93' }}>
                {currentStep === 'service' ? 'איזה שירות תרצה?' :
                 currentStep === 'datetime' ? 'מתי נוח לך?' :
                 'אשר את הפרטים'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-base font-medium"
              style={{ color: '#007AFF' }}
            >
              סגור
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="flex gap-2 mt-4">
            <div
              className="flex-1 h-1 rounded-full transition-all"
              style={{
                backgroundColor: currentStep === 'service' || currentStep === 'datetime' || currentStep === 'summary' ? '#007AFF' : '#E5E7EB'
              }}
            />
            <div
              className="flex-1 h-1 rounded-full transition-all"
              style={{
                backgroundColor: currentStep === 'datetime' || currentStep === 'summary' ? '#007AFF' : '#E5E7EB'
              }}
            />
            <div
              className="flex-1 h-1 rounded-full transition-all"
              style={{
                backgroundColor: currentStep === 'summary' ? '#007AFF' : '#E5E7EB'
              }}
            />
          </div>
        </div>

        {/* Content */}
        {currentStep === 'service' && (
          <div className="flex-1 overflow-y-auto p-6">
            <ServiceSelector
              businessId={businessId}
              selectedId={formData.serviceId}
              onSelect={handleServiceSelect}
            />
          </div>
        )}

        {currentStep === 'datetime' && businessId && formData.serviceId && (
          <div className="flex-1 overflow-y-auto p-6">
            <StaffDateTimeSelector
              businessId={businessId}
              selectedDate={formData.date}
              selectedTime={formData.time}
              selectedStaffId={formData.staffId}
              serviceId={formData.serviceId}
              onChange={handleDateTimeSelect}
              onBack={() => setCurrentStep('service')}
              staffFilter={staffFilter}
            />
          </div>
        )}

        {currentStep === 'summary' && (
          <AppointmentSummary
            data={{
              customerName: customerData?.name || 'אורח',
              customerPhone: customerData?.phone || '',
              customerEmail: customerData?.email,
              serviceId: formData.serviceId,
              serviceName: formData.serviceName,
              servicePrice: parseFloat(formData.servicePrice),
              serviceDuration: formData.serviceDuration,
              staffId: formData.staffId,
              staffName: formData.staffName,
              date: formData.date
            }}
            onEdit={(step) => {
              switch (step) {
                case 'service':
                  setCurrentStep('service');
                  setFormData(prev => ({
                    ...prev,
                    staffId: '',
                    staffName: '',
                    time: '09:00'
                  }));
                  break;
                case 'datetime':
                  setCurrentStep('datetime');
                  setFormData(prev => ({
                    ...prev,
                    time: ''
                  }));
                  break;
              }
            }}
            onConfirm={handleConfirm}
            loading={loading}
          />
        )}

        {/* Existing Appointment Modal - iOS Style */}
        <AnimatePresence>
          {showExistingAppointmentModal && existingAppointment && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowExistingAppointmentModal(false);
                  setLoading(false);
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white max-w-sm w-full overflow-hidden"
                style={{ borderRadius: '1.5rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Content */}
                <div className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-full"
                      style={{ backgroundColor: '#FF9F0A15' }}
                    >
                      <AlertTriangle className="h-8 w-8" style={{ color: '#FF9F0A' }} />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold mb-2" style={{ color: '#1C1C1E' }}>
                    {existingAppointment.is_same_day
                      ? 'תור קיים באותו היום'
                      : 'תור קיים בימים הקרובים'
                    }
                  </h3>

                  <div
                    className="p-4 rounded-2xl mb-4 text-right"
                    style={{ backgroundColor: '#FFF3CD' }}
                  >
                    <p className="text-sm" style={{ color: '#856404' }}>
                      יש לך תור <strong>{existingAppointment.service_name}</strong> עם <strong>{existingAppointment.staff_name}</strong>
                    </p>
                    <p className="text-sm mt-1" style={{ color: '#856404' }}>
                      {format(new Date(existingAppointment.start_time), 'EEEE, d בMMMM בשעה HH:mm', { locale: he })}
                    </p>
                  </div>

                  <p className="text-sm mb-6" style={{ color: '#8E8E93' }}>
                    {existingAppointment.is_same_day
                      ? 'בחירה בתור החדש תבטל את התור הקיים'
                      : 'האם ברצונך להוסיף תור נוסף?'
                    }
                  </p>
                </div>

                {/* Actions */}
                <div style={{ borderTop: '1px solid #E5E7EB' }}>
                  {existingAppointment.is_same_day ? (
                    <>
                      <button
                        onClick={() => createAppointment(true)}
                        className="w-full py-4 font-semibold"
                        style={{
                          borderBottom: '1px solid #E5E7EB',
                          color: '#007AFF',
                        }}
                      >
                        בחר בתור החדש
                      </button>
                      <button
                        onClick={() => {
                          setShowExistingAppointmentModal(false);
                          setLoading(false);
                        }}
                        className="w-full py-4 font-semibold"
                        style={{ color: '#8E8E93' }}
                      >
                        השאר את התור הקיים
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => createAppointment(true)}
                        className="w-full py-4 font-semibold"
                        style={{
                          borderBottom: '1px solid #E5E7EB',
                          color: '#FF3B30',
                        }}
                      >
                        בטל את התור הקיים וקבע תור חדש
                      </button>
                      <button
                        onClick={() => createAppointment(false)}
                        className="w-full py-4 font-semibold"
                        style={{
                          borderBottom: '1px solid #E5E7EB',
                          color: '#007AFF',
                        }}
                      >
                        השאר את התור הקיים וקבע תור נוסף
                      </button>
                      <button
                        onClick={() => {
                          setShowExistingAppointmentModal(false);
                          setLoading(false);
                        }}
                        className="w-full py-4 font-semibold"
                        style={{ color: '#8E8E93' }}
                      >
                        ביטול
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Auth Modal */}
        <AnimatePresence>
          {showAuth && businessId && (
            <CustomerAuth
              businessId={businessId}
              onClose={() => setShowAuth(false)}
              onSuccess={async () => {

                // בדוק מחדש את פרטי הלקוח
                let customerPhone = localStorage.getItem('customerPhone');

                if (!customerPhone) {

                  return;
                }

                // נסה למצוא עם הפורמט המקורי
                let { data: customer, error } = await supabase
                  .from('customers')
                  .select('*')
                  .eq('business_id', businessId)
                  .eq('phone', customerPhone)
                  .maybeSingle();

                // אם לא נמצא, נסה עם פורמט חלופי
                if (!customer && !error) {
                  let alternativePhone = customerPhone;

                  if (customerPhone.startsWith('+972')) {
                    alternativePhone = '0' + customerPhone.slice(4);

                  } else if (customerPhone.startsWith('0')) {
                    alternativePhone = '+972' + customerPhone.slice(1);

                  }

                  const result = await supabase
                    .from('customers')
                    .select('*')
                    .eq('business_id', businessId)
                    .eq('phone', alternativePhone)
                    .maybeSingle();

                  customer = result.data;
                  error = result.error;
                }

                if (!error && customer) {
                  setCustomerData(customer);
                  setFormData(prev => ({
                    ...prev,
                    customerId: customer.id,
                    customerName: customer.name,
                    customerPhone: customer.phone,
                    customerEmail: customer.email
                  }));
                  setShowAuth(false);
                  toast.success('התחברת בהצלחה!');

                  // בדוק אם יש URL שמור (חזרה מהתחברות עבור autofill)
                  const returnUrl = sessionStorage.getItem('returnToAfterAuth');
                  if (returnUrl) {
                    sessionStorage.removeItem('returnToAfterAuth');
                    // נווט חזרה ל-URL המלא עם כל ה-query params
                    setTimeout(() => {
                      window.location.href = returnUrl;
                    }, 500);
                    return;
                  }

                  // המשך לשלב הבא אוטומטית
                  if (currentStep === 'service' && formData.serviceId) {

                    setCurrentStep('datetime');
                  }
                  // אל תקרא ל-handleConfirm כאן - תן למשתמש ללחוץ על הכפתור
                }
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}